import { Outlet, useNavigate } from "react-router-dom";
import { Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAccount } from "@/contexts/account.context";
import { tenantAtivo } from "@/lib/hey-api-client";

interface ExigeLojaAtivaProps {
  /** Abre o seletor de contas do `Dashboard`. */
  onEscolherLoja: () => void;
}

/**
 * <M-263> As telas de dados só montam quando há uma loja ativa.
 *
 * A conta principal da agência **não tem tenant de propósito**: `applyAccountContext` apaga
 * `active_customer_uuid` quando a agência entra sem subconta, porque ela não tem dados
 * próprios. Mas as rotas de dados montavam assim mesmo, e cerca de trinta telas leem
 * `active_customer_uuid` direto do navegador. Com ele vazio, as chamadas saíam com
 * `customer_uuid=` e a API respondia `422` — a Visão Geral acusava "Conta não informada" e o
 * Benchmark mostrava "Erro ao carregar dados." atrás do seletor de contas.
 *
 * Consertar tela a tela espalharia a mesma condição por trinta arquivos, e a trigésima
 * primeira tela nasceria sem ela. Esta rota de layout é o lugar único: sem loja, nenhuma tela
 * de dados monta e nenhuma chamada sai; com loja, ela não existe.
 *
 * ## Quem é barrado
 *
 * Só quem **pode escolher** uma loja — agência ou dono de várias contas. Para o lojista de
 * uma loja só o tenant é gravado no login, e para o membro de time por `applyTeamContext`;
 * barrar esses casos num instante sem tenant mostraria um aviso que eles não têm como
 * resolver.
 *
 * ## Por que o tenant é lido a cada render
 *
 * `active_customer_uuid` mora no `localStorage`, que não avisa ninguém quando muda. Quem avisa
 * é o contexto de contas: `switchAccount` grava o tenant **antes** de `setSelectedAccount`, e
 * `clearSelectedAccount` o apaga antes de atualizar o estado. Os dois re-renderizam quem
 * consome `useAccount`, e é nesse render que a leitura acontece.
 *
 * ## Por que a troca de loja remonta as telas
 *
 * As telas de dados buscam em `useEffect` e guardam, em `ref`, a assinatura da última busca
 * para não repeti-la. A assinatura tem datas, período e filtros — **não** a loja. Numa troca
 * o efeito roda de novo, encontra a mesma assinatura e desiste: a Visão Geral seguia mostrando
 * o resumo e o funil da loja anterior até alguém recarregar a página.
 *
 * Pôr a loja na assinatura de cada tela seria o mesmo conserto em trinta arquivos. A `key` do
 * `Outlet` resolve de uma vez: loja nova, tela nova — `ref`, estado e número da loja anterior
 * vão embora juntos, e nada da outra loja fica à vista enquanto a busca nova não volta. A
 * chave espelha o `customer_uuid` que as telas mandam, e por isso a hidratação no login, que
 * grava o mesmo valor no estado e no navegador, não conta como troca.
 */
const ExigeLojaAtiva = ({ onEscolherLoja }: ExigeLojaAtivaProps) => {
  const {
    selectedAccount,
    accounts,
    canSwitchAccounts,
    isAgencyUser,
    isTeamScoped,
  } = useAccount();
  const navigate = useNavigate();

  const loja = selectedAccount?.customer_uuid || tenantAtivo();
  const podeEscolher = canSwitchAccounts || isAgencyUser;
  if (!podeEscolher || selectedAccount || loja) {
    return <Outlet key={loja ?? ""} />;
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md space-y-4 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Store className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Escolha uma loja</h2>
          <p className="text-sm text-muted-foreground">
            Esta tela mostra os dados de uma loja. Escolha qual loja você quer
            ver para continuar.
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {accounts.length > 0 && (
            <Button onClick={onEscolherLoja}>Escolher loja</Button>
          )}
          {isAgencyUser && !isTeamScoped && (
            <Button
              variant="outline"
              onClick={() => navigate("/dashboard/accounts")}
            >
              Contas da agência
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExigeLojaAtiva;
